import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import '../styles/ProductFamilyPage.css';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';

const ProductFamilyPage = () => {
  const { family } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Variantes d'animation
  const pageVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1, transition: { duration: 0.5 } },
  };

  const bannerVariants = {
    initial: { opacity: 0, scale: 1.1 },
    animate: { opacity: 1, scale: 1, transition: { duration: 0.8 } },
  };

  const overlayVariants = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0, transition: { delay: 0.3, duration: 0.5 } },
  };

  const productCardVariants = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const staggerChildren = {
    animate: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  
  // Récupérer les données des produits
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch('/data/data.json');
        const data = await response.json();
        
        const familyProducts = data.filter(
          product => product.family.toLowerCase() === family.toLowerCase()
        );
        
        setProducts(familyProducts);
        setLoading(false);
      } catch (error) {
        console.error('Erreur lors de la récupération des données produits:', error);
        setLoading(false);
      }
    };
    
    fetchProducts();
  }, [family]);
  
  // Obtenir le nom traduit de la famille
  const getFamilyTranslation = () => {
    const translations = {
      'bulldozer': 'Bulldozers',
      'loader': 'Chargeuses',
      'mini-excavator': 'Mini-Pelles',
      'excavator': 'Pelles Hydrauliques',
      'grader': 'Niveleuses'
    };
    
    return translations[family.toLowerCase()] || family;
  };
  
  // Obtenir la méta description pour le SEO
  const getMetaDescription = () => {
    const translations = {
      'bulldozer': 'Découvrez notre gamme de bulldozers LiuGong. Machines robustes et fiables pour tous vos travaux de terrassement.',
      'loader': 'Explorez notre sélection de chargeuses LiuGong de haute performance pour vos projets de construction et d\'exploitation.',
      'mini-excavator': 'Notre gamme de mini-pelles LiuGong combine puissance et maniabilité pour vos chantiers urbains et espaces restreints.',
      'excavator': 'Pelles hydrauliques LiuGong - puissantes, efficaces et fiables pour tous vos travaux d\'excavation et de construction.',
      'grader': 'Niveleuses LiuGong de précision pour l\'entretien des routes, le nivelage et la préparation des terrains.'
    };
    
    return translations[family.toLowerCase()] || `Découvrez notre gamme de ${getFamilyTranslation().toLowerCase()} LiuGong au Maroc.`;
  };
  
  // Télécharger la fiche technique PDF
  const downloadPDF = (model) => {
    window.open(`/assets/docs/${model}.pdf`, '_blank');
  };
  
  // Déterminer si une caractéristique est une valeur simple ou complexe (objet/tableau)
  const isSimpleValue = (value) => {
    return typeof value !== 'object' || value === null;
  };
  
  // Formater la clé de caractéristique pour l'affichage

  
  // Formater la clé de caractéristique pour l'affichage et la traduire
  const formatKey = (key) => {
    // Dictionnaire de traduction anglais -> français
    const translations = {
      // Caractéristiques générales
      'gross_power': 'Puissance brute',
      'net_power': 'Puissance nette',
      'operating_weight': 'Poids opérationnel',
      'engine_power': 'Puissance moteur',
      'max_torque': 'Couple maximal',
      'engine': 'Moteur',
      'emission_standard': 'Norme d\'émission',
      'cab': 'Cabine',
      'transmission': 'Transmission',
      'fuel_tank_capacity': 'Capacité du réservoir',
      'hydraulic_reservoir': 'Réservoir hydraulique',
      'dimensions': 'Dimensions',
      
      // Caractéristiques lames/godets
      'blade_capacity': 'Capacité de la lame',
      'blade_lift_height': 'Hauteur de levage de la lame',
      'blade_width': 'Largeur de la lame',
      'max_cut_depth': 'Profondeur de coupe max',
      'bucket_capacity': 'Capacité du godet',
      'bucket_force': 'Force du godet',
      'bucket_digging_force': 'Force d\'excavation du godet',
      'bucket_ripping_force': 'Force d\'arrachement du godet',
      'breakout_force': 'Force d\'arrachement',
      
      // Caractéristiques hydrauliques
      'hydraulic_pump_flow': 'Débit pompe hydraulique',
      
      // Vitesses et forces
      'ground_pressure': 'Pression au sol',
      'travel_speed_forward': 'Vitesse avant',
      'travel_speed_reverse': 'Vitesse arrière',
      'max_travel_speed_high': 'Vitesse max haute',
      'max_travel_speed_low': 'Vitesse max basse',
      'max_speed_forward': 'Vitesse max avant',
      'drawbar_pull': 'Force de traction',
      'arm_digging_force': 'Force d\'excavation du bras',
      'arm_ripping_force': 'Force d\'arrachement du bras',
      'rated_payload': 'Charge utile nominale',
      'rated_load': 'Charge nominale',
      'rated_power': 'Puissance nominale',
      'hydraulic_pump_flow_L_per_min': 'Débit de la pompe hydraulique L par min',
      
      // Dimensions et mesures
      'dump_height': 'Hauteur de déversement',
      'dump_reach': 'Portée de déversement',
      'dump_clearance': 'Hauteur de déversement',
      'hinge_pin_height': 'Hauteur de l\'axe',
      'cycle_time': 'Temps de cycle',
      'max_digging_depth': 'Profondeur d\'excavation max',
      'boom_length': 'Longueur de la flèche',
      'arm_options': 'Options de bras',
      'articulation_angle': 'Angle d\'articulation',
      'wheelbase': 'Empattement',
      'overall_length': 'Longueur totale',
      'cab_height': 'Hauteur de la cabine',
      'swing_speed': 'Vitesse de rotation'
    };
    
    // Séparer la clé et l'unité potentielle (comme _kg, _mm, etc.)
    const keyParts = key.split('_');
    const lastPart = keyParts[keyParts.length - 1].toLowerCase();
    const units = {
      'kg': '(kg)',
      'mm': '(mm)',
      'kw': '(kW)',
      'kn': '(kN)',
      'kmh': '(km/h)',
      'm3': '(m³)',
      'l': '(L)',
      'rpm': '(tr/min)',
      'deg': '(°)',
      'hp': '(ch)',
      'nm': '(Nm)',
      'kpa': '(kPa)',
      's': '(s)',
      'per_min': '(L/min)'
    };
    
    // Vérifier si la dernière partie est une unité
    const unit = units[lastPart] || '';
    
    // Enlever l'unité de la clé si elle existe
    const baseKey = unit ? key.substring(0, key.length - lastPart.length - 1) : key;
    
    // Rechercher la traduction
    let translated = translations[baseKey];
    
    // Si pas de traduction directe, essayer de traduire les parties individuelles
    if (!translated) {
      translated = baseKey
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
    
    return translated + (unit ? ' ' + unit : '');
  };
  
  const pageTitle = `${getFamilyTranslation()} LiuGong | LGM Maroc`;
  const metaDescription = getMetaDescription();
  
  if (loading) {
    return (
      <>
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={metaDescription} />
        </Helmet>
        <Navigation />
        <motion.div 
          className="product-family-page"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="loading">
            <motion.div
              animate={{ 
                rotate: 360,
                transition: { repeat: Infinity, duration: 1, ease: "linear" }
              }}
              style={{ width: 40, height: 40, borderRadius: '50%', border: '3px solid #f3f3f3', borderTop: '3px solid #00307d', margin: '0 auto 20px' }}
            />
            Chargement des produits...
          </div>
        </motion.div>
        <Footer />
      </>
    );
  }
  
  if (products.length === 0) {
    return (
      <>
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={metaDescription} />
        </Helmet>
        <Navigation />
        
        <motion.div 
          className="family-banner"
          variants={bannerVariants}
          initial="initial"
          animate="animate"
        >
          <img 
            src={`/assets/images/${family.toLowerCase()}_banner.png`}
            alt={`${getFamilyTranslation()} LiuGong`}
            onError={(e) => {
              e.target.src = '/assets/images/default_banner.png';
              e.target.alt = 'Équipements LiuGong';
            }}
          />
          <motion.div 
            className="banner-overlay"
            variants={overlayVariants}
            initial="initial"
            animate="animate"
          >
            <h1>{getFamilyTranslation()}</h1>
            <p>Découvrez notre gamme de {getFamilyTranslation().toLowerCase()}</p>
          </motion.div>
        </motion.div>
        
        <motion.div 
          className="product-family-page"
          variants={pageVariants}
          initial="initial"
          animate="animate"
        >
          <motion.div 
            className="no-products"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
          >
            <h2>Aucun produit trouvé pour cette famille</h2>
            <p>Veuillez vérifier le nom de la famille ou contacter l'administrateur.</p>
          </motion.div>
        </motion.div>
        <Footer/>
      </>
    );
  }
  
  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={`/assets/images/${family.toLowerCase()}_banner.png`} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={`https://lgm.ma/products/${family}`} />
      </Helmet>
      <Navigation />
      
      <motion.div 
        className="family-banner"
        variants={bannerVariants}
        initial="initial"
        animate="animate"
      >
        <img 
          src={`/assets/images/${family.toLowerCase()}_banner.png`}
          alt={`${getFamilyTranslation()} LiuGong`}
          onError={(e) => {
            e.target.src = '/assets/images/default_banner.png';
            e.target.alt = 'Équipements LiuGong';
          }}
          loading="eager"
        />
        <motion.div 
          className="banner-overlay"
          variants={overlayVariants}
          initial="initial"
          animate="animate"
        >
          <h1>{getFamilyTranslation()}</h1>
          <p>Découvrez notre gamme de {getFamilyTranslation().toLowerCase()}</p>
        </motion.div>
      </motion.div>
      
      <motion.div 
        className="product-family-page"
        variants={pageVariants}
        initial="initial"
        animate="animate"
      >
        <motion.div 
          className="products-container"
          variants={staggerChildren}
          initial="initial"
          animate="animate"
        >
          {products.map((product, index) => (
            <motion.div 
              key={product.model} 
              className="product-card"
              variants={productCardVariants}
              whileHover={{ 
                y: -5, 
                boxShadow: "0 10px 25px rgba(0, 0, 0, 0.08)",
                transition: { duration: 0.3 }
              }}
            >
              <div className={`product-main ${index % 2 === 0 ? 'even' : 'odd'}`}>
                <motion.div 
                  className="product-image"
                  initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                >
                  <img 
                    src={`/assets/images/${product.model}.png`} 
                    alt={`${product.model} - ${product.title} LiuGong`}
                    onError={(e) => {
                      e.target.src = '/assets/images/product-placeholder.png';
                      e.target.alt = 'Image non disponible';
                    }}
                    loading={index === 0 ? "eager" : "lazy"}
                  />
                </motion.div>
                
                <motion.div 
                  className="product-info"
                  initial={{ opacity: 0, x: index % 2 === 0 ? 30 : -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5, duration: 0.5 }}
                >
                  <motion.h2 
                    id={`product-${product.model}`}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6, duration: 0.3 }}
                  >
                    {product.title}
                  </motion.h2>
                  
                  <motion.div 
                    className="model-tag"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.7, duration: 0.3 }}
                  >
                    {product.model}
                  </motion.div>
                  
                  <motion.p 
                    className="product-description"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.8, duration: 0.5 }}
                  >
                    {product.description}
                  </motion.p>
                  
                  <motion.button 
                    className="download-button"
                    onClick={() => downloadPDF(product.model)}
                    aria-label={`Télécharger la fiche technique du ${product.model}`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.9, duration: 0.3 }}
                  >
                    <svg viewBox="0 0 24 24" width="24" height="24" fill="#fff" aria-hidden="true">
                      <path d="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z" />
                    </svg>
                    Télécharger la fiche technique
                  </motion.button>
                </motion.div>
              </div>
              
              <motion.div 
                className="product-specs"
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.5 }}
              >
                <motion.h3
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8, duration: 0.3 }}
                >
                  Caractéristiques techniques
                </motion.h3>
                
                <div className="specs-table">
                  {Object.entries(product.characteristics).map(([key, value], specIndex) => {
                    if (isSimpleValue(value)) {
                      return (
                        <motion.div 
                          className="spec-row" 
                          key={key}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.9 + (specIndex * 0.03), duration: 0.2 }}
                        >
                          <div className="spec-label">{formatKey(key)}</div>
                          <div className="spec-value">
                            {value === true ? 'Oui' : value === false ? 'Non' : value}
                          </div>
                        </motion.div>
                      );
                    } else if (Array.isArray(value)) {
                      return (
                        <motion.div 
                          className="spec-row" 
                          key={key}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.9 + (specIndex * 0.03), duration: 0.2 }}
                        >
                          <div className="spec-label">{formatKey(key)}</div>
                          <div className="spec-value">{value.join(', ')}</div>
                        </motion.div>
                      );
                    } else {
                      // Pour les objets imbriqués, créer plusieurs lignes
                      return (
                        <motion.div 
                          className="spec-group" 
                          key={key}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.9 + (specIndex * 0.03), duration: 0.2 }}
                        >
                          <div className="spec-group-title">{formatKey(key)}</div>
                          {Object.entries(value).map(([subKey, subValue], subIndex) => (
                            <motion.div 
                              className="spec-row nested" 
                              key={`${key}-${subKey}`}
                              initial={{ opacity: 0, x: 5 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 1 + (subIndex * 0.02), duration: 0.2 }}
                            >
                              <div className="spec-label">{formatKey(subKey)}</div>
                              <div className="spec-value">{subValue}</div>
                            </motion.div>
                          ))}
                        </motion.div>
                      );
                    }
                  })}
                </div>
              </motion.div>
              
              {index < products.length - 1 && (
                <motion.div 
                  className="product-divider"
                  initial={{ opacity: 0, scaleX: 0 }}
                  animate={{ opacity: 0.7, scaleX: 1 }}
                  transition={{ delay: 1, duration: 0.5 }}
                ></motion.div>
              )}
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
      <Footer/>
    </>
  );
};

export default ProductFamilyPage;